import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";
import Loader from "../../../components/Loader/Loader";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost, apiPut } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./PlanForm.module.scss";

const INITIAL_FORM = {
  planName: "",
  price: "",
  credits: "",
  isPopular: false,
  orderBy: "",
  isActive: true,
};

const PlanForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================= */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     API CALLS
  ========================= */
  const fetchPlan = useCallback(async () => {
    try {
      const res = await apiGet("catalog", `/plans/${id}`);
      if (res?.status === "success") {
        const d = res.data;
        setForm({
          planName: d.planName || "",
          price: d.price ?? "",
          credits: d.credits ?? "",
          isPopular: Boolean(d.isPopular),
          orderBy: d.orderBy ?? "",
          isActive: d.isActive ?? true,
        });
      }
    } catch {
      showNotification("Failed to load plan details", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) fetchPlan();
  }, [isEdit, fetchPlan]);

  /* =========================
     VALIDATION & HANDLERS
  ========================= */
  const validate = () => {
    const errs = {};
    if (!form.planName.trim()) errs.planName = "Plan name is required";
    if (form.price === "" || form.price < 0)
      errs.price = "Valid price is required";
    if (form.credits === "" || form.credits < 0)
      errs.credits = "Valid credits value is required";
    if (form.orderBy === "" || form.orderBy < 1)
      errs.orderBy = "Order (min 1) is required";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = {
          planName: form.planName.trim(),
          price: Number(form.price),
          credits: Number(form.credits),
          orderBy: Number(form.orderBy),
          isActive: form.isActive,
        };
        const res = await apiPut("catalog", `/plans/${id}`, payload);
        if (res?.status === "success") {
          showNotification("Plan updated successfully", "success");
          setTimeout(() => navigate("/admin/plans"), 1200);
        } else {
          showNotification(res?.message || "Update failed", "error");
        }
      } else {
        const payload = {
          planName: form.planName.trim(),
          price: Number(form.price),
          credits: Number(form.credits),
          isPopular: form.isPopular ? "MOST POPULAR" : null,
          orderBy: Number(form.orderBy),
          isActive: form.isActive,
        };
        const res = await apiPost("catalog", "/plans", payload);
        if (res?.status === "success") {
          showNotification("Plan created successfully", "success");
          setTimeout(() => navigate("/admin/plans"), 1200);
        } else {
          showNotification(res?.message || "Creation failed", "error");
        }
      }
    } catch (err) {
      try {
        const parsed = JSON.parse(err.message);
        showNotification(parsed?.message || "Something went wrong", "error");
      } catch {
        showNotification("Something went wrong", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  if (loading) return <Loader />;

  return (
    <div className={styles["admin-plan-form__page"]}>
      {notification && (
        <div className={styles["admin-plan-form__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-plan-form__header"]}>
        <Link to="/admin/plans" className={styles["admin-plan-form__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["admin-plan-form__title-group"]}>
          <h2 className={styles["admin-plan-form__title"]}>
            {isEdit ? "Edit Plan" : "Add New Plan"}
          </h2>
          <p className={styles["admin-plan-form__subtitle"]}>
            {isEdit
              ? "Update the details and pricing for this subscription tier."
              : "Fill in the details to create a new subscription tier."}
          </p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className={styles["admin-plan-form__card"]}>
        <form onSubmit={handleSubmit} noValidate>
          {/* INPUTS GRID */}
          <div className={styles["admin-plan-form__grid"]}>
            <Input
              label="Plan Name"
              name="planName"
              placeholder="e.g. Starter Pack"
              value={form.planName}
              onChange={handleChange}
              error={errors.planName}
              required
            />

            <Input
              label="Price (₹)"
              type="number"
              name="price"
              placeholder="e.g. 1000"
              value={form.price}
              onChange={handleChange}
              error={errors.price}
              required
            />

            <Input
              label="Credits Allocation"
              type="number"
              name="credits"
              placeholder="e.g. 1000"
              value={form.credits}
              onChange={handleChange}
              error={errors.credits}
              required
            />

            <Input
              label="Sort Order"
              type="number"
              name="orderBy"
              placeholder="e.g. 1"
              value={form.orderBy}
              onChange={handleChange}
              error={errors.orderBy}
              required
            />
          </div>

          {/* TOGGLES SECTION */}
          <div className={styles["admin-plan-form__toggles-section"]}>
            <div className={styles["admin-plan-form__toggle-row"]}>
              <div className={styles["admin-plan-form__toggle-info"]}>
                <strong>Active Status</strong>
                <span>
                  Plan is visible and available to users for purchase.
                </span>
              </div>
              <ToggleSwitch
                isOn={form.isActive}
                onToggle={() =>
                  setForm((prev) => ({ ...prev, isActive: !prev.isActive }))
                }
              />
            </div>

            {!isEdit && (
              <div className={styles["admin-plan-form__toggle-row"]}>
                <div className={styles["admin-plan-form__toggle-info"]}>
                  <strong>Mark as Most Popular</strong>
                  <span>
                    Highlights this plan on the pricing page with a distinct
                    badge.
                  </span>
                </div>
                <ToggleSwitch
                  isOn={form.isPopular}
                  onToggle={() =>
                    setForm((prev) => ({ ...prev, isPopular: !prev.isPopular }))
                  }
                />
              </div>
            )}
          </div>

          {/* ACTIONS */}
          <div className={styles["admin-plan-form__actions"]}>
            <button
              type="button"
              className={`${styles["admin-plan-form__btn"]} ${styles["admin-plan-form__btn--secondary"]}`}
              onClick={() => navigate("/admin/plans")}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["admin-plan-form__btn"]} ${styles["admin-plan-form__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting
                ? isEdit
                  ? "Updating Plan..."
                  : "Creating Plan..."
                : isEdit
                  ? "Update Plan"
                  : "Create Plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanForm;
